import React from 'react';
import { Link } from 'react-router-dom';
import { formatShortDateTime } from '../../utils/dateFormatter';
import { GRID_TEXT, offersText as t } from '../../config/availabilityGridLabels';
import {
  accountRuleText,
  promotionText,
  ruleDetail,
  ruleName,
  ruleValue
} from '../pricing/airbnbOfferText';

/**
 * «على Airbnb» in the night popup of «التقويم» (tasks/ops-calendar-airbnb-offers.md): what Airbnb is
 * running for this unit, as it bears on a guest arriving this night. READ-ONLY, and every judgement is
 * the server's — the stale flag, the promotion that cancels the discounts, and the arrival dates each
 * lead-time discount reaches (`appliesFrom` / `appliesTo`, business dates). The page only compares
 * date strings, as the grid already does for its bars.
 *
 * Airbnb only, and said so: Booking.com and Gathern discounts are not read. No price after discount —
 * it depends on stay length, lead time and booking count, and would be wrong most of the time.
 */

const reachesNight = (rule, date) =>
  (!rule.appliesFrom || date >= rule.appliesFrom) && (!rule.appliesTo || date <= rule.appliesTo);

const Heading = () => (
  <div className="ag-pop__offers-head">
    <span className="ag-pop__offers-title">{GRID_TEXT.offersHeading}</span>
    <span className="ag-pop__muted">{t('pricing.offers.airbnbOnly')}</span>
  </div>
);

const NightAirbnbOffers = ({ offers, date, unitId, canOpenPricing }) => {
  if (offers.status === 'not_read_yet' || offers.status === 'not_on_account') {
    return (
      <section className="ag-pop__offers" aria-label={GRID_TEXT.offersHeading}>
        <Heading />
        <div className="ag-pop__muted">
          {offers.status === 'not_read_yet' ? GRID_TEXT.offersNotReadYet : GRID_TEXT.offersNotOnAccount}
        </div>
      </section>
    );
  }
  if (offers.status !== 'ok') return null;

  const promotions = Array.isArray(offers.runningPromotions) ? offers.runningPromotions : [];
  const rules = (Array.isArray(offers.rules) ? offers.rules : []).filter(rule => reachesNight(rule, date));
  const adjustments = Array.isArray(offers.account?.rateAdjustments) ? offers.account.rateAdjustments : [];
  // The server's rule (a running promotion replaces the listing's discounts). The discounts stay
  // listed, crossed out, as on the pricing card: ops must see what returns when the promotion ends.
  const cancelled = Boolean(offers.promotionCancelsDiscounts) && rules.length > 0;

  return (
    <section className="ag-pop__offers" aria-label={GRID_TEXT.offersHeading}>
      <Heading />
      {offers.stale && <div className="ag-pop__note">{t('pricing.offers.staleTitle')}</div>}
      {/* As on the card: an unpublished listing sells nothing on Airbnb, whatever its offers say. */}
      {offers.published === false && <div className="ag-pop__note">{t('pricing.offers.unpublished')}</div>}

      {promotions.map(p => (
        <div key={p.type} className="ag-pop__offer ag-pop__offer--promo">{promotionText(p, t)}</div>
      ))}
      {cancelled && <div className="ag-pop__note">{t('pricing.offers.overrideTitle')}</div>}

      {rules.map(rule => {
        const detail = ruleDetail(rule.type, rule.days, t, true);
        return (
          <div key={rule.key} className={`ag-pop__offer${cancelled ? ' ag-pop__offer--cancelled' : ''}`}>
            <span className="ag-pop__offer-name">{ruleName(rule.type, rule.days, t)}</span>
            {' '}
            <span className="ag-pop__offer-value">{ruleValue(rule, t)}</span>
            {detail && <span className="ag-pop__offer-detail">{detail}</span>}
          </div>
        );
      })}
      {!promotions.length && !rules.length && <div className="ag-pop__muted">{GRID_TEXT.offersNoneTonight}</div>}

      {/* Always a line, even with no rule — as on the card: the +15% coming off matters as much as it going on. */}
      {(adjustments.length ? adjustments : [null]).map(a => (
        <div key={a ? `${a.mode}-${a.value}` : 'none'} className="ag-pop__offer">
          {a ? accountRuleText(a, t) : t('pricing.offers.noAccountRule')}
        </div>
      ))}

      <div className="ag-pop__muted">
        {offers.lastReadAt && <>{t('pricing.offers.lastRead')} {formatShortDateTime(offers.lastReadAt)}</>}
        {offers.lastReadAt && canOpenPricing && ' · '}
        {canOpenPricing && <Link to={`/pricing-assistant?property=${unitId}`} className="ag-pop__link">{GRID_TEXT.openPricing}</Link>}
      </div>
    </section>
  );
};

export default NightAirbnbOffers;
